import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  IconButton,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../../sections/user';
// mock
import instance from '../../api/axiosRequest';
import validatePhone from '../../utils/validatePhone';
import { saveEmailData } from '../../features/campaign/campaignSlice';
import BackButton from '../../components/BackButton';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'date', label: 'Enquiry Date', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy = 'createdAt') {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.email.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function User() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const { email: emailData } = useSelector((state) => state.campaign);

  const [page, setPage] = useState(0);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('desc');

  const [selected, setSelected] = useState(emailData.selected || []);
  const [selectedRows, setSelectedRows] = useState([]);

  const [orderBy, setOrderBy] = useState('createdAt');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const getEnquiries = async () => {
      setLoading(true);
      try {
        const { data } = await instance.get(`/enquiry/getEnquiry?vendor_id=${user.id}`);
        setEnquiries(data.data.rows);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error.response);
      }
    };
    getEnquiries();
  }, [user]);

  useEffect(() => {
    const rows = enquiries.filter((e) => selected.includes(e.id));
    setSelectedRows(
      rows.map((e) => ({
        ID: e.id,
        Name: e.name,
        Email: e.email,
        Phone: e.phone,
        'Enquiry Date': new Date(e.createdAt).toLocaleString(),
        ...e.form_details,
      }))
    );
  }, [enquiries, selected]);

  useEffect(() => {
    const rows = enquiries.filter((e) => selected.includes(e.id));
    const toEmailAddresses = rows.map((e) => e.email);
    dispatch(saveEmailData({ toEmailAddresses, selected, to: rows }));
  }, [selected]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = enquiries.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - enquiries.length) : 0;

  const filteredUsers = applySortFilter(enquiries, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Page title="User">
      <Container>
        {Boolean(searchParams.get('hideHeader')) && <BackButton />}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Enquiry
          </Typography>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            selected={selectedRows}
            filterName={filterName}
            onFilterName={handleFilterByName}
            hideHeader={Boolean(searchParams.get('hideHeader'))}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={enquiries.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                {!loading && (
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const { id, name, createdAt, city, email, phone } = row;
                      const isItemSelected = selected.indexOf(id) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} onChange={(event) => handleClick(event, id)} />
                          </TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            {id}
                          </TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            {name}
                          </TableCell>
                          <TableCell align="left">
                            {email}
                            <a
                              style={{
                                textDecoration: 'none',
                                color: '#212B36',
                              }}
                              href={`mailto:${email}`}
                            >
                              <IconButton>
                                <MailOutlineIcon />
                              </IconButton>
                            </a>
                          </TableCell>
                          <TableCell align="left">
                            {validatePhone(phone)}
                            <IconButton onClick={() => window.open(`https://wa.me/${validatePhone(phone)}`, '_blank')}>
                              <WhatsAppIcon />
                            </IconButton>
                            <a
                              style={{
                                textDecoration: 'none',
                                color: '#212B36',
                              }}
                              href={`tel:${validatePhone(phone)}`}
                            >
                              <IconButton>
                                <LocalPhoneIcon />
                              </IconButton>
                            </a>
                          </TableCell>
                          <TableCell align="left">{city}</TableCell>
                          <TableCell align="left">{new Date(createdAt).toLocaleString()}</TableCell>
                          <TableCell align="left">
                            <Label variant="ghost" color="success">
                              {sentenceCase('Pending')}
                            </Label>
                          </TableCell>

                          <TableCell align="right">
                            <UserMoreMenu row={row} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                )}

                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        {loading ? <CircularProgress /> : <SearchNotFound searchQuery={filterName} />}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                {/* {!loading && <Stack sx={{width: "100%", height: 100}} justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Stack>} */}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={enquiries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
